import type React from 'react';
import {
  Box,
  Button,
  Icon,
  Link,
  Tag,
  Text,
  Title,
} from '@nimbus-ds/components';
import { ExternalLinkIcon, InvoiceIcon } from '@nimbus-ds/icons';
import { CurrencyType, PaymentResponseDto } from '@tiendanube/common';
import { Country, Status as StatusEnum } from '@tiendanube/common/src/enums';
import { CurrencyPrice } from 'commons/components';
import { getShouldShowInstallments } from 'domains/Orders/Orders/utils/OrderDetailsPage/utils';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import RejectedNuvemPagoPaymentInfo from './RejectedNuvemPagoPaymentInfo';

interface PaymentMethodDetailProps {
  payment: PaymentResponseDto;
  currency: CurrencyType;
  openGatewayLink: (gatewayLink: string) => void;
  isMultiplePayments: boolean;
  isNuvemPagoPayment: boolean;
  onNuvemPagoRedirect: () => void;
  country: Country;
  isMobile: boolean;
  openNuvemPago: () => void;
}

function PaymentMethodDetail({
  payment: {
    method,
    installments,
    capturedAmount,
    refundedAmount,
    gateway,
    gatewayId,
    status,
    gatewayLink,
  },
  currency,
  openGatewayLink,
  isMultiplePayments,
  isNuvemPagoPayment,
  onNuvemPagoRedirect,
  country,
  isMobile,
  openNuvemPago,
}: Readonly<PaymentMethodDetailProps>): React.JSX.Element {
  const t = useTranslationOrders();

  const normalizedInstallments = Number.isFinite(Number(installments))
    ? Math.max(1, Number(installments))
    : 1;

  const showInstallments = getShouldShowInstallments(
    normalizedInstallments,
    method,
  );

  const hasAnyRefund = Number(refundedAmount?.value ?? 0) > 0;

  const isNuvemPagoRejected =
    isNuvemPagoPayment && status === StatusEnum.WARNIG_TRANSACTIONS;

  const hideNuvemPagoLink = country === Country.BR && isMobile;

  const trimmedMethod = method.trim();

  return (
    <Box display="flex" flexDirection="column" gap="1" width="full">
      {!!gateway && !isMultiplePayments && (
        <Title as="h5" color="neutral-textHigh">
          {t(`gateway.${gateway}`, { defaultValue: gateway })}
        </Title>
      )}
      {isMultiplePayments && (
        <Box paddingY="1">
          <CurrencyPrice
            price={Number(capturedAmount?.value ?? 0)}
            currency={currency}
          />
        </Box>
      )}
      <Box display="flex" alignItems="center">
        {!!trimmedMethod && (
          <Box display="block" paddingRight="2">
            <Tag>{trimmedMethod}</Tag>
          </Box>
        )}
        {showInstallments && (
          <Text as="p" color="neutral-textLow">
            {t('cardPayment.installments', {
              count: normalizedInstallments,
            })}
          </Text>
        )}
      </Box>
      {!!gatewayId && (
        <Box display="flex" flexWrap="wrap" gap="1" alignItems="center">
          <Text color="neutral-textLow" as="span">
            {t('cardPayment.gatewayId')}
          </Text>
          {gatewayLink && !isNuvemPagoRejected ? (
            <Link
              appearance="primary"
              as="button"
              onClick={() => openGatewayLink(gatewayLink)}
            >
              {gatewayId}
              <Icon color="currentColor" source={<ExternalLinkIcon />} />
            </Link>
          ) : (
            <Text as="span" color="neutral-textHigh">
              {gatewayId}
            </Text>
          )}
        </Box>
      )}

      {isNuvemPagoRejected && !hideNuvemPagoLink && (
        <RejectedNuvemPagoPaymentInfo
          gatewayLink={gatewayLink}
          openGatewayLink={openGatewayLink}
          openNuvemPago={openNuvemPago}
        />
      )}

      {isNuvemPagoPayment && hasAnyRefund && (
        <Box display="block" paddingY="2">
          <Button appearance="neutral" onClick={onNuvemPagoRedirect}>
            <Icon color="currentColor" source={<InvoiceIcon />} />
            {t('cardPayment.refundReceipt')}
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default PaymentMethodDetail;
